import * as math from "./math";
import type { SrsPair, ISRS, ICoords } from "./types";
import parseCsv from "csv-parse/lib/sync";

function csvIsValid(parsedCsv: number[][]): boolean {
  return parsedCsv[0].length === 4;
}

function parseGcpCsv(csv: string) {
  const parsedWithCommas = parseCsv(csv, { delimiter: "," });
  if (csvIsValid(parsedWithCommas)) {
    return parsedWithCommas;
  }
  const parsedWithTabs = parseCsv(csv, { delimiter: "\t" });
  if (csvIsValid(parsedWithTabs)) {
    return parsedWithTabs;
  }
  throw new Error("Invalid CSV");
}

export function convertCsv(srsPair: SrsPair, srs: ISRS, csv: string): string {
  const parsedCsv = parseGcpCsv(csv);

  return parsedCsv
    .map((cols: string[]) => {
      const name = cols[0];
      const coords = {
        y: parseFloat(cols[1]),
        x: parseFloat(cols[2]),
        z: parseFloat(cols[3]),
      };
      const { x, y, z } = convert(srsPair, srs, coords);
      return `${name},${y},${x},${z}`;
    })
    .join("\n");
}

export function convert(srsPair: SrsPair, srs: ISRS, coords: ICoords): ICoords {
  if (srsPair[0] === "assumed") {
    if (srsPair[1] === "global") {
      return convertFromAssumedToGlobal(srs, coords);
    }
    if (srsPair[1] === "wgs84") {
      return convertFromAssumedToWgs84(srs, coords);
    }
    throw new Error(`Invalid SRS pair, ${JSON.stringify(srsPair)}`);
  }

  if (srsPair[0] === "global") {
    if (srsPair[1] === "assumed") {
      return convertFromGlobalToAssumed(srs, coords);
    }
    if (srsPair[1] === "wgs84") {
      return convertFromGlobalToWgs84(srs, coords);
    }
    throw new Error(`Invalid SRS pair, ${JSON.stringify(srsPair)}`);
  }

  if (srsPair[0] === "wgs84") {
    if (srsPair[1] === "assumed") {
      return convertFromWgs84ToAssumed(srs, coords);
    }
    if (srsPair[1] === "global") {
      return convertFromWgs84ToGlobal(srs, coords);
    }
    throw new Error(`Invalid SRS pair, ${JSON.stringify(srsPair)}`);
  }

  throw new Error(`Invalid SRS pair, ${JSON.stringify(srsPair)}`);
}

function convertFromAssumedToGlobal(srs: ISRS, coords: ICoords): ICoords {
  const { assumedSrs } = srs;
  if (!assumedSrs) {
    throw new Error("Can't convert from Assumed SRS. SRS is not assumed");
  }
  return math.convertAssumedToGlobal(
    assumedSrs.translationMatrixToIntermediateSrs,
    coords,
  );
}

function convertFromAssumedToWgs84(srs: ISRS, coords: ICoords): ICoords {
  const { assumedSrs } = srs;
  if (!assumedSrs) {
    throw new Error("Can't convert from Assumed SRS. SRS is not assumed");
  }
  const globalCoords = math.convertAssumedToGlobal(
    assumedSrs.translationMatrixToIntermediateSrs,
    coords,
  );
  return math.convertGlobalToWgs84(
    assumedSrs.intermediateSrsProj4,
    globalCoords,
  );
}

function convertFromGlobalToAssumed(srs: ISRS, coords: ICoords): ICoords {
  const { assumedSrs } = srs;
  if (!assumedSrs) {
    throw new Error("Can't convert to Assumed SRS. SRS is not assumed");
  }
  return math.convertGlobalToAssumed(
    assumedSrs.translationMatrixToIntermediateSrs,
    coords,
  );
}

function convertFromGlobalToWgs84(srs: ISRS, coords: ICoords): ICoords {
  const { assumedSrs, globalSrs } = srs;
  if (assumedSrs) {
    return math.convertGlobalToWgs84(assumedSrs.intermediateSrsProj4, coords);
  }
  if (globalSrs) {
    return math.convertGlobalToWgs84(globalSrs.proj4, coords);
  }
  throw new Error("Invalid SRS");
}

function convertFromWgs84ToGlobal(srs: ISRS, coords: ICoords): ICoords {
  const { assumedSrs, globalSrs } = srs;
  if (assumedSrs) {
    return math.convertWgs84ToGlobal(assumedSrs.intermediateSrsProj4, coords);
  }
  if (globalSrs) {
    return math.convertWgs84ToGlobal(globalSrs.proj4, coords);
  }
  throw new Error("Invalid SRS");
}

function convertFromWgs84ToAssumed(srs: ISRS, coords: ICoords): ICoords {
  const { assumedSrs } = srs; // flow happy
  if (!assumedSrs) {
    throw new Error("Can't convert to Assumed SRS. SRS is not assumed");
  }
  const globalCoords = math.convertWgs84ToGlobal(
    assumedSrs.intermediateSrsProj4,
    coords,
  );
  return math.convertGlobalToAssumed(
    assumedSrs.translationMatrixToIntermediateSrs,
    globalCoords,
  );
}
